// pages/monitor/panels/peak/PeakWiring.js —— 削峰填谷 · 主接线图（依据《云平台单站控制（削峰填谷）需求文档》p3）
// 面板内以 Segmented 切换「强电图 / 拓扑图」两个子页；两图均带完整总线连接与功率方向箭头。
// 图例：红色惊叹号 = 该节点/设备有告警；虚线 + 断线标记 = 通信离线。
import React, { useState } from 'react';
import { Checkbox, Radio, Select, Space, Tag } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import { getDevices, getOverview, TOPO_NODES, TOPO_LINKS, TOPO_SHOW_OPTIONS } from '../../../../config/stationData';
import './peak.css';

// 箭头 marker（按方向着色）
const Arrows = () => (
  <defs>
    <marker id="pk-fwd" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
      <path d="M0,0 L9,4.5 L0,9 z" fill="#00d4ff" />
    </marker>
    <marker id="pk-rev" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
      <path d="M0,0 L9,4.5 L0,9 z" fill="#52e8b0" />
    </marker>
    <marker id="pk-warn" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto">
      <path d="M0,0 L9,4.5 L0,9 z" fill="#ff5a7f" />
    </marker>
  </defs>
);

// 告警惊叹号标记
const WarnMark = ({ x, y }) => (
  <g>
    <circle cx={x} cy={y} r={8} fill="#ff5a7f" />
    <text x={x} y={y + 4} textAnchor="middle" className="pk-warn-mark">!</text>
  </g>
);

// 强电图 · 节点数据面板（对齐截图 p003_i001：P/Q/S/PF/U/I 详细电气量，白字左对齐）
const Panel = ({ x, y, w, title, rows, alarm, color }) => {
  const h = 40 + rows.length * 14;
  return (
    <g>
      <rect className="pk-panel" x={x} y={y} width={w} height={h} rx={8}
        style={alarm ? { stroke: '#ff5a7f', strokeWidth: 1.4 } : undefined} />
      <text className="pk-pt" x={x + 12} y={y + 20}>{title}</text>
      {rows.map((r, i) => (
        <text key={i} className="pk-pl" x={x + 14} y={y + 36 + i * 14}>
          <tspan className="pk-pk">{r[0]}</tspan>
          <tspan className="pk-pv" dx={8}>{r[1]}</tspan>
        </text>
      ))}
      {alarm ? <WarnMark x={x + w - 8} y={y - 4} /> : null}
    </g>
  );
};

// 状态标签（并网绿 / 静置蓝 / 告警红）
const StatusTag = ({ x, y, text, fill }) => (
  <g>
    <rect x={x} y={y} width={text.length * 12 + 12} height={16} rx={8} fill={fill} opacity={0.18} />
    <text className="pk-tag" x={x + 6} y={y + 12} fill={fill}>{text}</text>
  </g>
);

const StrongTopo = ({ station, ov, dev, meterConf, showGrid }) => {
  const W = 1200;
  const H = 640;
  const busY = 300;
  const offline = station.comm === 'offline';
  const pcsAlarm = dev.pcs.some((p) => p.alarm);
  const rackAlarm = dev.racks.some((r) => r.alarm);
  const charging = ov.storagePower > 0;

  // 计量/关口总表
  const m0 = dev.meters[0];
  // 负荷出线电表（三相供电）
  const mL = dev.meters[dev.meters.length - 1];
  const p0 = dev.pcs[0];
  const r0 = dev.racks[0];

  const meterRows = (m, name) => [
    ['P', `${m.p} kW`],
    ['Q', `${m.q} kVar`],
    ['S', `${Math.hypot(m.p, m.q).toFixed(1)} kVA`],
    ['PF', `${m.pf}`],
    ['U', `${m.u.join(' / ')} V`],
    ['I', `${m.i.join(' / ')} A`],
  ];

  // 节点盒：名称 + 状态标签 + 可选告警红标
  const node = (x, y, w, h, title, tag, color, alarm, cx) => {
    cx = cx === undefined ? x + w / 2 : cx;
    return (
      <g key={title}>
        <rect className="ef2-box" x={x} y={y} width={w} height={h} rx={10}
          style={alarm ? { stroke: '#ff5a7f', strokeWidth: 1.6 } : undefined} />
        <text className="ef2-node-name" x={cx} y={y + 22} textAnchor="middle">{title}</text>
        {tag ? <StatusTag x={x + w / 2 - tag.length * 6} y={y + h - 20} text={tag} fill={color || '#52e8b0'} /> : null}
        {alarm ? <WarnMark x={x + w - 4} y={y - 4} /> : null}
      </g>
    );
  };

  return (
    <div className="ef2-wrap">
      <svg className="ef2-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="削峰填谷强电接线图">
        <Arrows />
        {/* 交流母线（红色主干，对齐 p003_i001 主母线） */}
        <line className="ef2-bus" x1={60} y1={busY} x2={W - 60} y2={busY}
          strokeDasharray={offline ? '6 6' : undefined} />
        <text className="ef2-bus-label" x={60} y={busY - 12}>交流母线 0.4 kV{offline ? '（通信离线）' : ''}</text>

        {/* 上排：电网总表 → 变压器（高压侧） */}
        {showGrid ? node(40, 90, 190, 86, '总表（关口）', '计量', '#00d4ff', false) : null}
        {showGrid ? <Panel x={40} y={186} w={210} title="总表（关口）" rows={meterRows(m0, '总表')} /> : null}
        <line className="ef2-link" x1={showGrid ? 250 : 60} y1={133} x2={330} y2={133}
          markerEnd={m0.p > 0 ? 'url(#pk-fwd)' : 'url(#pk-rev)'}
          strokeDasharray={offline ? '6 6' : undefined} />
        {node(330, 90, 190, 86, '1#变压器', '运行', '#52e8b0', false)}
        <Panel x={330} y={186} w={210} title="1#变压器" rows={meterRows(m0, '变压器')} />

        {/* 变压器 → 母线 */}
        <line className="ef2-link" x1={425} y1={176} x2={425} y2={busY - 4} markerEnd="url(#pk-fwd)"
          strokeDasharray={offline ? '6 6' : undefined} />

        {/* 光伏（可选） */}
        {station.has.pv ? (
          <>
            {node(920, 40, 180, 80, '光伏节点', '发电', '#f4c542', false)}
            <Panel x={920} y={128} w={200} title="光伏节点" rows={[['P', `${ov.pvPower} kW`], ['U', '三相 / V'], ['I', '三相 / A']]} />
            <line className="ef2-link" x1={1010} y1={120} x2={1010} y2={busY - 4} markerEnd="url(#pk-fwd)" />
          </>
        ) : null}

        {/* 负载节点（上分支）：绿色向上功率箭头（消耗） */}
        <line className="ef2-link" x1={700} y1={120} x2={700} y2={busY - 4} markerEnd="url(#pk-rev)"
          strokeDasharray={offline ? '6 6' : undefined} />
        {node(612, 40, 176, 80, '1#负载', '消耗', '#ff5a7f', false)}
        <Panel x={612} y={128} w={210} title="1#负载" rows={meterRows(mL, '负载')} />

        {/* 储能支路（下分支）：PCS → 电池堆，纵向并列 */}
        <line className="ef2-link" x1={560} y1={busY} x2={560} y2={366}
          markerEnd={charging ? 'url(#pk-rev)' : 'url(#pk-fwd)'} strokeDasharray={offline ? '6 6' : undefined} />
        <text className="ef2-node-sub" x={578} y={344}>{charging ? '充电：母线 → PCS' : '放电：PCS → 母线'}</text>
        {node(480, 366, 160, 84, `PCS ${p0.id}`, p0.grid === '离网' ? '离网' : '并网', p0.grid === '离网' ? '#52e8b0' : '#52e8b0', pcsAlarm)}
        <Panel x={660} y={366} w={210} title="pcs_0" rows={[
          ['P', `${p0.p} kW`],
          ['U', `${p0.uabc.join(' / ')} V`],
          ['I', `${p0.iabc.join(' / ')} A`],
          ['状态', p0.grid === '离网' ? '离网' : '并网'],
        ]} alarm={pcsAlarm} />
        <line className="ef2-link" x1={560} y1={450} x2={560} y2={486} strokeDasharray={offline ? '6 6' : undefined} />
        {node(480, 486, 160, 84, `bms ${r0.id}`, r0.state === '放电' ? '放电' : r0.state === '充电' ? '充电' : '静置', r0.state === '放电' ? '#52e8b0' : '#00aaff', rackAlarm)}
        <Panel x={660} y={486} w={210} title="电池堆" rows={[
          ['U', `${r0.voltage} V`],
          ['I', `${r0.current} A`],
          ['SOC', `${r0.soc} %`],
        ]} alarm={rackAlarm} />

        {/* 储能表（按「储能表配置」三选一展示与否/关联哪只表） */}
        {meterConf === 'none' ? (
          <text className="ef2-node-sub" x={980} y={busY + 20} textAnchor="middle">电表已隐藏（储能表配置：不展示）</text>
        ) : (
          <>
            <line className="ef2-link" x1={1000} y1={busY} x2={1000} y2={366} markerEnd="url(#pk-fwd)"
              strokeDasharray={offline ? '6 6' : undefined} />
            {(() => {
              const single = meterConf === 'single';
              const m = single ? (dev.meters[1] || dev.meters[0]) : dev.meters[0];
              return (
                <>
                  {node(920, 366, 170, 84, `${single ? '单柜电表' : '并网柜电表'} ${m.id}`, '计量', '#4dd8ff', false)}
                  <Panel x={920} y={460} w={220} title={single ? '单柜电表' : '并网柜电表'} rows={meterRows(m, '储能表')} />
                </>
              );
            })()}
          </>
        )}

        {/* 通信离线断线标记 */}
        {offline ? <g><line x1={560} y1={512} x2={600} y2={530} stroke="#ff5a7f" strokeWidth={2} />
          <line x1={600} y1={512} x2={560} y2={530} stroke="#ff5a7f" strokeWidth={2} /></g> : null}
      </svg>
    </div>
  );
};

// -------------------- 拓扑图 --------------------
const TopoView = ({ station, ov, dev }) => {
  const [shown, setShown] = useState(TOPO_SHOW_OPTIONS.filter((o) => o.checked).map((o) => o.key));
  const [view, setView] = useState('overview'); // 缩略图 / 拓扑图 视图切换（截图 p003_i002 左上）
  const valueOf = (type) => {
    const parts = [];
    if (shown.includes('power')) parts.push(`P ${type === 'pcs' ? '−250 kW' : type === 'battery' ? `−${dev.racks.reduce((s, r) => s + r.current, 0) * 0.7} kW` : `${ov.loadPower} kW`}`);
    if (shown.includes('soc') && (type === 'battery' || type === 'pcs')) parts.push(`SOC ${ov.soc}%`);
    if (shown.includes('voltage')) parts.push('U 398 V');
    if (shown.includes('current')) parts.push('I 212 A');
    if (shown.includes('temp') && type === 'battery') parts.push('T 29 ℃');
    if (shown.includes('state')) parts.push('运行');
    return parts;
  };
  const nodeMap = {};
  TOPO_NODES.forEach((n) => { nodeMap[n.key] = n; });

  // 节点图元（缩略图/拓扑图共用绘制；overview=紧凑无数据，detail=含实时数据）
  const NodeGlyph = ({ n, detail }) => {
    const vals = valueOf(n.type);
    const alarm = shown.includes('alarm') && (n.type === 'battery' || n.type === 'pcs') && dev.pcs.some((p) => p.alarm);
    const boxStyle = { ...(alarm ? { stroke: '#ff5a7f' } : {}) };
    return (
      <g key={n.key}>
        <rect className="ef2-box" x={n.x} y={n.y} width={120} height={detail ? 44 : 34} rx={8} style={boxStyle} />
        <text className="ef2-node-name" x={n.x + 60} y={n.y + 16} textAnchor="middle" style={{ fontSize: 12 }}>{n.name}</text>
        {detail ? (
          <text className="ef2-node-sub" x={n.x + 60} y={n.y + 30} textAnchor="middle" style={{ fontSize: 10 }}>
            {vals.slice(0, 2).join(' · ')}
          </text>
        ) : null}
        {alarm ? <WarnMark x={n.x + 116} y={n.y + 2} /> : null}
      </g>
    );
  };

  const svg = (detail) => (
    <div className="ef2-wrap">
      <svg className="ef2-svg" viewBox="0 0 900 420" role="img" aria-label="削峰填谷拓扑图">
        <Arrows />
        {TOPO_LINKS.map((l) => {
          const a = nodeMap[l.from];
          const b = nodeMap[l.to];
          const arrow = shown.includes('alarm') ? 'url(#pk-fwd)' : undefined;
          return <line key={`${l.from}-${l.to}`} className="ef2-link" x1={a.x + 60} y1={a.y + 16}
            x2={b.x} y2={b.y + 16} markerEnd={arrow} style={!detail ? { strokeDasharray: '3 3' } : undefined} />;
        })}
        {TOPO_NODES.map((n) => <NodeGlyph key={n.key} n={n} detail={detail} />)}
      </svg>
    </div>
  );

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <ScrollTabs
      activeKey={view}
      onChange={setView}
      items={[
        {
          key: 'overview',
          label: '缩略图',
          children: (
            <>
              {svg(false)}
              <div className="peak-note">缩略图：现场接线全览（虚线为连接关系）。点击切换到「拓扑图」查看节点实时数据与告警。</div>
            </>
          ),
        },
        {
          key: 'detail',
          label: '拓扑图',
          children: (
            <>
              <div className="peak-toolbar">
                <span className="peak-toolbar-label">实时数据展示项（可勾选）</span>
                <Checkbox.Group
                  value={shown}
                  onChange={setShown}
                  options={TOPO_SHOW_OPTIONS.map((o) => ({ label: o.label, value: o.key }))}
                />
              </div>
              {svg(true)}
              <div className="peak-note">拓扑图数据项来自「系统管理 · 拓扑图配置」，此处仅控制实时数据的展示；有功功率与 SOC 单位分别为 kW、%。</div>
            </>
          ),
        },
      ]}
    />
  );
};

const PeakWiring = ({ station }) => {
  const ov = getOverview(station.id);
  const dev = getDevices(station.id);
  const [meterConf, setMeterConf] = useState('grid'); // 储能表配置：并网柜电表 / 单柜电表 / 不展示（p44–45 单选）
  const [showGrid, setShowGrid] = useState(true);

  const legend = (
    <div className="panel">
      <div className="panel-title">图例<small>接线状态与指向说明</small></div>
      <Space size={24} wrap>
        <span style={{ fontSize: 12 }}><span className="dot online" />正常（实线 · 功率方向箭头）</span>
        <span style={{ fontSize: 12 }}><Tag color="red" style={{ marginRight: 6 }}>!</Tag>节点/设备存在告警</span>
        <span style={{ fontSize: 12 }}><Tag style={{ marginRight: 6 }}>╳</Tag>通信离线（虚线 + 断线标记）</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>箭头指向 = 当前功率流向（青色：母线→设备，绿色：设备→母线）</span>
      </Space>
    </div>
  );

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <ScrollTabs
      items={[
        {
          key: 'strong',
          label: '强电图',
          children: (
            <>
              <div className="peak-toolbar">
                <Space size={16} wrap>
                  <span className="peak-toolbar-label">储能表配置：</span>
                  <Radio.Group value={meterConf} onChange={(e) => setMeterConf(e.target.value)}>
                    <Radio value="grid">并网柜电表</Radio>
                    <Radio value="single">单柜电表</Radio>
                    <Radio value="none">不展示</Radio>
                  </Radio.Group>
                  <Checkbox checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)}>展示电网节点</Checkbox>
                  <Select size="small" value={dev.pcs[0] && dev.pcs[0].id} style={{ width: 150 }}
                    options={dev.pcs.map((p) => ({ value: p.id, label: `切换设备：${p.name}` }))} />
                </Space>
              </div>
              <div className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-title">强电图<small>一次接线与功率方向 · 单位 kW / kWh</small></div>
                <StrongTopo station={station} ov={ov} dev={dev} meterConf={meterConf} showGrid={showGrid} />
              </div>
              {legend}
            </>
          ),
        },
        {
          key: 'topo',
          label: '拓扑图',
          children: (
            <>
              <div className="panel" style={{ marginBottom: 16 }}>
                <div className="panel-title">拓扑图<small>节点实时数据与告警标识</small></div>
                <TopoView station={station} ov={ov} dev={dev} />
              </div>
              {legend}
            </>
          ),
        },
      ]}
    />
  );
};

export default PeakWiring;
